import { useEffect, useState, useRef } from "react";
import "ol/ol.css";
import "ol-ext/dist/ol-ext.css";
import "@fortawesome/fontawesome-free/css/all.css";
import api from '../services/auth';
import { Map, View } from "ol";
import { defaults as defaultControls } from "ol/control";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import { fromLonLat, toLonLat } from "ol/proj";
import ControlBar from "ol-ext/control/Bar";
import Toggle from "ol-ext/control/Toggle";
import Button from "ol-ext/control/Button";
import { Draw, Select } from "ol/interaction";
import { Fill, Stroke, Style, Icon } from "ol/style";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Point } from "ol/geom";
import { Feature } from "ol";
import { GeoJSON } from "ol/format";
import logger from '../logger';
import * as turf from '@turf/turf';
import LayerSwitcherImage from "ol-ext/control/LayerSwitcherImage";
import XYZ from "ol/source/XYZ";
import { fetchSources, fetchSourceParameters } from '../services/api';
import ScaleLine from 'ol/control/ScaleLine';
import MousePosition from 'ol/control/MousePosition';
import { createStringXY } from 'ol/coordinate';
import Overlay from 'ol/Overlay';
import SideBar from './SideBar';
import FeaturePopup from './FeaturePopup';
import "../App.css";

function NonFuelAreas() {
    const [location, setLocation] = useState({
        longitude: 11.028995965569194,
        latitude: 46.05321545463036,
    });

    const [drawnLon, setDrawnLon] = useState(null);
    const [drawnLat, setDrawnLat] = useState(null);
    const [manualLon, setManualLon] = useState(''); 
    const [manualLat, setManualLat] = useState(''); 
    const [radius, setRadius] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [latError, setLatError] = useState('');
    const [lonError, setLonError] = useState('');
    const [radiusError, setRadiusError] = useState('');
    const [startDateError, setStartDateError] = useState('');
    const [endDateError, setEndDateError] = useState('');
    const [confirmedRadius, setConfirmedRadius] = useState('');
    const [confirmedStartDate, setConfirmedStartDate] = useState('');
    const [confirmedEndDate, setConfirmedEndDate] = useState('');
    const [showDialog, setShowDialog] = useState(false);
    const [mapLoading, setMapLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    // const drawnFeatureRef = useRef(null);
    const drawPointToggleRef = useRef(null);
    const drawPointInteractionRef = useRef(null);
    const geoJsonLayerRef = useRef(null);
    const geoJsonDataRef = useRef(null);
    const tempFeatureRef = useRef(null);
    const lastCircleFeatureRef = useRef(null);
    const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('access'));    
    const [isDrawerOpen, setDrawerOpen] = useState(true);
    const [isButtonLoading, setButtonLoading] = useState(false); // Add loading state for the Confirm button
    const dialogRef = useRef(null); // Ref for the dialog element
    const [abortController, setAbortController] = useState(null);
    const isButtonLoadingRef = useRef(isButtonLoading);  // Use a ref to track loading state
    const selectToViewRef = useRef(null);    
    const [hoveredButtonInfo, setHoveredButtonInfo] = useState('<strong>Hover over a button to see its details</strong>'); // State for hover information
    const [hoverLock, setHoverLock] = useState(false); // State to lock hover info
    const [dialogOpen, setDialogOpen] = useState(false); // Track if any dialog is open
    const hoverLockRef = useRef(false);  // Use a ref to track hover lock state
    const showDialogRef = useRef(false);  // Ref to track dialog state
    const popupOverlayRef = useRef(null);
    const [isMapInitialized, setIsMapInitialized] = useState(false);

    const updateHoverButtonInfo = (content, forceUpdate = false) => {
        // Use showDialogRef for immediate access to dialog state
        if (!hoverLockRef.current || forceUpdate) {
            if (!showDialogRef.current) {  // Prevent updating hover info if dialog is open
                setHoveredButtonInfo(content);
            }
        }
    };

    const toggleDrawer = () => {
        setDrawerOpen((prevState) => {
            const newState = !prevState;
            logger.log("is drawer open: ", newState);
            return newState;
        });
    };

    // Monitor `showDialog` state to control hover behavior and locking
    useEffect(() => {
        if (showDialog) {
            setHoverLock(true);
            hoverLockRef.current = true;  // Use ref to immediately reflect hover lock
            showDialogRef.current = true;  // Update showDialogRef when dialog is shown
        } else {
            setHoverLock(false);
            hoverLockRef.current = false; // Unlock hover immediately
            showDialogRef.current = false; // Update showDialogRef when dialog is hidden
        }
    }, [showDialog]);

    // Helper function to format today's date in YYYY-MM-DD format
    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0'); // GetMonth is zero-based
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Default start date: 01 January 2015
    const defaultStartDate = `${new Date().getFullYear()}-01-01`;

    const combineSourcesWithCategories = async () => {
        try {
            const sources = await fetchSources();  // Replace with your own sources fetching logic
            const parameters = await fetchSourceParameters();  // Replace with your API for source parameters

            const projectionMap = parameters.projection_systems.reduce((map, proj) => {
                map[proj.id] = proj.title;
                return map;
            }, {});

            const sourceTypeMap = parameters.source_types.reduce((map, type) => {
                map[type.id] = { title: type.title, options: type.options };
                return map;
            }, {});

            return sources.map(source => {
                const projection = projectionMap[source.projection];
                const sourceType = sourceTypeMap[source.type];
                const sourceOptions = source.options.reduce((acc, option) => {
                    acc[option.title] = option.value;
                    return acc;
                }, {});

                return {
                    ...source,
                    projection,
                    sourceType,
                    sourceOptions,
                };
            }).filter(source => source !== null);

        } catch (error) {
            logger.error('Error combining sources:', error);
            throw error;
        }
    };

    const assignTileLayerToLayerSwitcherImage = (combinedSources, map) => {
        combinedSources
            .filter(source => source.is_enabled)
            .forEach(source => {
                let layer = null;
                if (source.sourceType.title.includes('Tile')) {
                    layer = new TileLayer({
                        source: new XYZ({
                            url: source.sourceOptions.url,
                            tileSize: parseInt(source.sourceOptions.tileSize) || 256,
                            attributions: source.attribution,
                        }),
                        title: source.title,
                        visible: false,
                        displayInLayerSwitcherImage: true,
                    });
                }

                // Check if the layer was created correctly before adding it to the map
                if (layer) {
                    map.addLayer(layer);
                } 
                
            });
    };

    // Update the ref whenever the loading state changes
    useEffect(() => {
        isButtonLoadingRef.current = isButtonLoading;

        logger.log("draw point toggle ref: ", drawPointToggleRef.current);

        if (isButtonLoading) {
            // Remove the drawPoint interaction if loading is true
            if (mapRef.current && drawPointInteractionRef.current) {
                mapRef.current.removeInteraction(drawPointInteractionRef.current);
            }
        } else if (!isButtonLoading && drawPointToggleRef.current?.getActive()) {
            // Add the drawPoint interaction back if loading is false
            if (mapRef.current && drawPointInteractionRef.current) {
                mapRef.current.addInteraction(drawPointInteractionRef.current);
            }
        }

    }, [isButtonLoading]);

    useEffect(() => {
        setMapLoading(true);

        // Load saved BBOX from localStorage if available
        const savedBbox = localStorage.getItem('mapBbox');

        // Initialize the map
        const map = new Map({
            target: "map",
            controls: defaultControls({ zoom: false }).extend([
                new ScaleLine({
                    units: 'metric',
                    bar: true,
                    steps: 4,
                    text: true,
                    minWidth: 140,
                    maxWidth: 140,
                }),
                new MousePosition({
                    coordinateFormat: createStringXY(4), // Formats the coordinates to 4 decimal places
                    projection: 'EPSG:4326', // Set projection to WGS84 (latitude/longitude)
                    className: 'mouse-position', // Class name for styling
                    target: document.getElementById('mouse-position'), // The DOM element to display the mouse position
                    undefinedHTML: '&nbsp;', // What to display when the mouse is outside the map
                })
            ]),
            layers: [
                new TileLayer({
                    source: new OSM(),
                    title: "Map",
                    displayInLayerSwitcher: false,
                    displayInLayerSwitcherImage: true,
                }),
            ],
            view: new View({
                center: fromLonLat([location.longitude, location.latitude]),
                zoom: 5,
            }),
        });

        mapRef.current = map;

        selectToViewRef.current = new Select({
            filter: (feature) => {
                // Only allow selection for features with `selectable !== false`
                return feature.get('selectable') !== false;
            },
        });
        
        if (selectToViewRef.current) {
            mapRef.current.addInteraction(selectToViewRef.current);
        }

        console.log("MapRef current (after initialization):", mapRef.current);

        setIsMapInitialized(true); 

        // If a BBOX is saved, fit the map view to this BBOX
        if (savedBbox) {
            const bbox = JSON.parse(savedBbox);
            const extent = bbox.map(coord => parseFloat(coord)); // Parse the bbox array
            map.getView().fit(extent, { size: map.getSize() });
        }

        // Save the current BBOX to localStorage whenever the map is moved or zoomed
        map.on('moveend', () => {
            const extent = map.getView().calculateExtent(map.getSize());
            localStorage.setItem('mapBbox', JSON.stringify(extent));
        });

        // Fetch and combine sources with categories (e.g., from API)
        combineSourcesWithCategories()
        .then(combinedSources => {
        // Assign tile layers to LayerSwitcherImage
        assignTileLayerToLayerSwitcherImage(combinedSources, map);

        // Create the LayerSwitcherImage control
        const layerSwitcherImage = new LayerSwitcherImage({        
            reordering: false,
            mouseover: false,
            collapsed: true,
            toggleOne: true,
            displayInLayerSwitcher: (layer) => layer instanceof TileLayer,
        });

        // Function to set a default image for the main button
        const setDefaultMainButtonImage = () => {
            const mainButton = document.querySelector('.ol-layerswitcher-image > button');
            if (mainButton) {
            mainButton.style.backgroundImage = 'url(https://tile.openstreetmap.org/10/512/349.png)';
            mainButton.style.backgroundSize = 'cover';
            mainButton.style.backgroundColor = 'transparent';
            mainButton.textContent = '';
            mainButton.title = 'Map'; // Default tooltip
            }
        };

        // Function to update the main button image and tooltip based on the current layer
        const updateMainButtonImage = (layer) => {
            const mainButton = document.querySelector('.ol-layerswitcher-image > button');
            if (!mainButton) return;

            const listItem = Array.from(document.querySelectorAll('.ol-imgcontainer'))
            .find(li => li.querySelector('p').textContent.trim() === layer.get('title'));

            if (listItem) {
            const imgElement = listItem.querySelector('img');
            if (imgElement && imgElement.src) {
                mainButton.style.backgroundImage = `url(${imgElement.src})`;
                mainButton.style.backgroundSize = 'cover';
                mainButton.style.backgroundColor = 'transparent';
                mainButton.textContent = '';
                mainButton.title = layer.get('title'); // Tooltip for main button
                return;
            }
            }

            // If no matching layer or image found, set the default image
            setDefaultMainButtonImage();
        };

        // Add the LayerSwitcher control to the map
        map.addControl(layerSwitcherImage);

        // After the control has been added to the map, modify its className
        layerSwitcherImage.element.className += ' custom-layer-switcher-image';

        // Add event listener to handle layer selection
        document.querySelectorAll('.ol-layerswitcher-image li').forEach(item => {
            item.addEventListener('click', () => {
            // Add the collapsed class to hide the layer switcher
            layerSwitcherImage.element.classList.add('ol-collapsed');
            });
        });

        // Ensure the button is always visible
        const ensureButtonVisible = () => {
            const mainButton = document.querySelector('.ol-layerswitcher-image > button');
            if (mainButton) {
            mainButton.style.display = 'block'; // Force button display
            mainButton.style.opacity = '1';    // Ensure fully visible
            mainButton.style.visibility = 'visible';
            }
        };

        // Ensure button visibility after render
        layerSwitcherImage.once('rendercomplete', ensureButtonVisible);

        // Maintain button visibility when collapsed
        layerSwitcherImage.on('collapse', ensureButtonVisible);

        layerSwitcherImage.on('expand', ensureButtonVisible);

        // Get the initially visible layer (default map layer)
        const initialLayer = map.getLayers().getArray().find(layer => layer.getVisible());
        if (initialLayer) {
            updateMainButtonImage(initialLayer);
        } else {
            setDefaultMainButtonImage();
        }

        // Ensure the button updates correctly after rendercomplete
        layerSwitcherImage.once('rendercomplete', () => {
            // Handle the initially visible layer
            const visibleLayer = map.getLayers().getArray()
              .find(layer => layer?.getVisible && layer.getVisible());
            if (visibleLayer) {
              updateMainButtonImage(visibleLayer);
            } else {
              setDefaultMainButtonImage();
            }
          
            // Iterate over each li element in the layer switcher
            document.querySelectorAll('.ol-layerswitcher-image .panel-container ul > li').forEach(li => {
              // Find the title from <p> within the current li
              const titleElement = li.querySelector('p');
              if (!titleElement) return; // Skip if no <p> tag found
          
              // Clean the title
              const layerTitle = titleElement.textContent.trim().replace(/^TS - /, '') || 'Unnamed Layer';
          
              // Set tooltip for the image
              const imgElement = li.querySelector('img');
              if (imgElement && imgElement.src) {
                imgElement.title = layerTitle; // Tooltip for the image
              }
          
              // Set tooltip for the li element
              li.title = layerTitle; // Tooltip for the list item
            });
        });  

        // Enforce single-layer visibility and update the button image when layers toggle
        let isTogglingLayer = false;

        layerSwitcherImage.on('layer:visible', function (evt) {
            if (isTogglingLayer) return;
            isTogglingLayer = true;

            const selectedLayer = evt.layer;
            map.getLayers().forEach(l => {
            if (l === selectedLayer) {
                l.setVisible(true);
                updateMainButtonImage(l);

                // Add `selected-layer` class to the corresponding list item
                const listItem = Array.from(document.querySelectorAll('.ol-imgcontainer'))
                .find(li => li.querySelector('p').textContent.trim() === l.get('title'));
                if (listItem) {
                listItem.classList.add('selected-layer');
                }
            } else if (l.get('displayInLayerSwitcherImage')) {
                l.setVisible(false);

                // Remove `selected-layer` class from non-selected layers
                const listItem = Array.from(document.querySelectorAll('.ol-imgcontainer'))
                .find(li => li.querySelector('p').textContent.trim() === l.get('title'));
                if (listItem) {
                listItem.classList.remove('selected-layer');
                }
            }
            });

            isTogglingLayer = false;

            layerSwitcherImage.render();

            // Collapse the Layer Switcher Image after selection
            document.querySelector('.ol-layerswitcher-image').classList.add('collapsed');
        });
        })
        .catch(error => {
        logger.error("Error during the assignment process:", error);
        });

        // Set up vector layer to display drawn points and circles
        const pointSource = new VectorSource();
        const pointLayer = new VectorLayer({
            source: pointSource,
            style: new Style({
                fill: new Fill({
                    color: 'rgba(45, 85, 255, 0.5)',
                }),
                stroke: new Stroke({
                    color: '#023747', // Border
                    width: 1,
                }),
            }),
        });
        map.addLayer(pointLayer);       

        // Initialize a marker feature for manual input
        const manualMarker = new Feature({
            geometry: new Point(fromLonLat([location.longitude, location.latitude])),
        });

        pointSource.addFeature(manualMarker);

        markerRef.current = manualMarker;

        // Define a style for the drawn point (pin)
        const pinStyle = new Style({
            image: new Icon({
                anchor: [0.5, 1], // Position the pin at the point
                src: 'https://cdn-icons-png.flaticon.com/512/684/684908.png', // Example pin icon URL
                scale: 0.05, // Adjust the size of the pin
            }),
        });

        // Set up draw interaction for points
        drawPointInteractionRef.current = new Draw({
            type: "Point",
            source: pointSource,  // Add to the vector source
            condition: function (event) {
                if (isButtonLoading) {
                    return false;  // Prevent drawing when button is loading
                }
                return event.originalEvent.button === 0;  // Allow left mouse click to draw
            }
        });

        // Handle panning for right mouse button
        mapRef.current.on('pointermove', function (event) {
            if (event.originalEvent.buttons === 2) { // Check if the right mouse button is held
                mapRef.current.getViewport().style.cursor = 'grabbing';
                mapRef.current.getView().setCenter(
                    mapRef.current.getView().getCenter()
                );
            }
        });

        mapRef.current.on('pointerup', function (event) {
            if (event.originalEvent.button === 2) {  // Check if the right mouse button was released
                mapRef.current.getViewport().style.cursor = '';  // Reset cursor
            }
        });

        // Add event listener for right-click to prevent drawing and allow panning
        mapRef.current.getViewport().addEventListener('contextmenu', (event) => {
            event.preventDefault();  // Disable context menu on right-click
        });

        // Clear points before starting to draw a new one, but keep the circle
        drawPointInteractionRef.current.on("drawstart", function () {
            const pointSource = mapRef.current.getLayers().item(1).getSource(); // Assuming pointSource is the second layer

            // Get all features in the source
            const features = pointSource.getFeatures();

            // Remove only point features, leave the circle
            features.forEach((feature) => {
                const geometry = feature.getGeometry();
                if (geometry instanceof Point) {
                    pointSource.removeFeature(feature); // Remove only the point (pin) features
                }
            });

            // Remove select interaction to prevent selecting features while drawing
            if (selectToViewRef.current) {
                mapRef.current.removeInteraction(selectToViewRef.current);
            }
        });

        // Event handler for when drawing ends
        drawPointInteractionRef.current.on("drawend", function (event) {
            if (isButtonLoading) {
                return;  // Prevent updating pin's coordinates if loading is true
            }

            // Proceed only if not loading
            // Store the temporary feature for later removal
            tempFeatureRef.current = event.feature;  // Track the temporary feature for removal when Escape is pressed

            // Get the coordinates of the new point
            const coords = event.feature.getGeometry().getCoordinates();
            const [lon, lat] = toLonLat(coords);

            // Update state with new point's coordinates
            setDrawnLon(lon);
            setDrawnLat(lat);
            setManualLon(lon);
            setManualLat(lat);

            // Apply the pin style to the new feature
            event.feature.setStyle(pinStyle);

            // Re-add select interaction after drawing is complete
            if (selectToViewRef.current) {
                mapRef.current.addInteraction(selectToViewRef.current);
            }
        });

        // Draw Circle Toggle example
        drawPointToggleRef.current = new Toggle({
            html: '<i class="fa fa-map-marker-alt"></i>',
            className: "draw-circle",
            title: "Draw Circle",
            active: false,
            onToggle: function (active) {
                if (active) {
                    mapRef.current.addInteraction(drawPointInteractionRef.current);
                    setShowDialog(true);
                    setDialogOpen(true); // Set dialog state
                    setHoverLock(true);  // Lock hover info
                    updateHoverButtonInfo('<i class="fa fa-map-marker-alt"></i> <strong>Draw Circle:</strong> Click to activate the tool, then click anywhere on the map to place a point. A dialog will appear where you can adjust the latitude and longitude, specify a radius for the circle in kilometers, and choose a start and end date for filtering data.</p>', true);
                } else {
                    mapRef.current.removeInteraction(drawPointInteractionRef.current);
                    setShowDialog(false);
                    setDialogOpen(false); // Close dialog
                    setHoverLock(false);  // Unlock hover info
                    updateHoverButtonInfo('<strong>Hover over a button to see its details</strong>');
                }
            },
        });


        drawPointToggleRef.current.element.addEventListener('mouseenter', () => {
            if (!hoverLockRef.current) {
                updateHoverButtonInfo('<i class="fa fa-map-marker-alt"></i> <strong>Draw Circle:</strong> Click to activate the tool, then click anywhere on the map to place a point. A dialog will appear where you can adjust the latitude and longitude, specify a radius for the circle in kilometers, and choose a start and end date for filtering data.</p>', true);
            } else {
            }
        });

        drawPointToggleRef.current.element.addEventListener('mouseleave', () => {
            if (!hoverLockRef.current) {
                updateHoverButtonInfo('<strong>Hover over a button to see its details</strong>');
            }
        });

        const downloadButton = new Button({
            html: '<i class="fas fa-download"></i>',
            title: 'Download GeoJSON',
            handleClick: downloadGeoJSON,
        });

        // Add mouse enter and leave event listeners to download button
        downloadButton.element.addEventListener('mouseenter', () => {
            setHoveredButtonInfo('<i class="fas fa-download"></i> <strong>Download GeoJSON:</strong> Click to download a .geojson file that contains all the non-fuel areas within the selected radius and date range. You can use this file for analysis, sharing, or visualizing the data in other applications that support GeoJSON files.');
        });
        downloadButton.element.addEventListener('mouseleave', () => {
            setHoveredButtonInfo('<strong>Hover over a button to see its details</strong>');
        });       

        const baseControlBar = new ControlBar({
            controls: [
                drawPointToggleRef.current,
                downloadButton,
            ],
            orientation: "horizontal",
            className: "base-control-bar",
        });

        baseControlBar.setPosition("top-center");
        map.addControl(baseControlBar);

        setMapLoading(false);

        return () => {
            document.removeEventListener("keydown", onKeydown);
            if (mapRef.current) {
                mapRef.current.setTarget(null); // Clean up the map instance
            }

            mapRef.current.getViewport().removeEventListener('contextmenu', (event) => { });
        };
    }, [location]);

    const onKeydown = (event) => {
        // Check if loading is true and prevent the Escape functionality if loading
        if (isButtonLoadingRef.current) {
            return;  // If loading, do nothing when Escape is pressed
        }

        if (event.key === "Escape" && showDialogRef.current) {
            // Deactivate the draw point toggle
            if (drawPointToggleRef.current) {
                drawPointToggleRef.current.setActive(false);
            }

            // Remove the draw interaction from the map
            if (drawPointInteractionRef.current) {
                mapRef.current.removeInteraction(drawPointInteractionRef.current);
            }

            // Remove the temporary pin if it exists
            const pointSource = mapRef.current.getLayers().item(1).getSource(); // Assuming pointSource is the second layer
            if (tempFeatureRef.current) {
                pointSource.removeFeature(tempFeatureRef.current); // Remove the temporary feature
                tempFeatureRef.current = null; // Reset the ref after removal
            }

            // Remove the drawn point (if any) based on drawnLon and drawnLat
            if (drawnLon && drawnLat) {
                const features = pointSource.getFeatures();
                features.forEach((feature) => {
                    const coords = toLonLat(feature.getGeometry().getCoordinates());
                    const [lon, lat] = coords;

                    // Remove the pin that matches the drawnLon and drawnLat (temporary pin)
                    if (lon === drawnLon && lat === drawnLat) {
                        pointSource.removeFeature(feature); // Remove the feature (temporary pin)
                        setDrawnLon(null); // Reset drawn coordinates
                        setDrawnLat(null);
                    }
                });
            }

            // Close the dialog
            setShowDialog(false);
            showDialogRef.current = false;
        }
    };

    useEffect(() => {
        document.addEventListener("keydown", onKeydown);
        return () => {
            document.removeEventListener("keydown", onKeydown);
        };
    }, []);

    // Function to update the marker's position based on manual input
    useEffect(() => {
        if (manualLat && manualLon) {
            const coords = fromLonLat([parseFloat(manualLon), parseFloat(manualLat)]);
            markerRef.current.setGeometry(new Point(coords));
            mapRef.current.getView().setCenter(coords); // Optionally center the map on the new point
        }
    }, [manualLat, manualLon]);

    // Set default values for startDate and endDate when the dialog opens
    useEffect(() => {
        if (showDialog) {
            if (!startDate) setStartDate(defaultStartDate);
            if (!endDate) setEndDate(getTodayDate());
        }
    }, [showDialog]);

    const flattenGeoJSON = (geoJsonData) => {
        // Create an array to store the flattened features
        let flattenedFeatures = [];

        // Helper function to recursively flatten FeatureCollections
        const flatten = (geoJson) => {
            if (geoJson.type === 'FeatureCollection') {
                geoJson.features.forEach((feature) => {
                    if (feature.type === 'FeatureCollection') {
                        flatten(feature); // Recursively flatten nested FeatureCollections
                    } else {
                        flattenedFeatures.push(feature); // Add non-nested features to the array
                    }
                });
            } else {
                flattenedFeatures.push(geoJson); // If it's already a Feature, just add it
            }
        };

        flatten(geoJsonData); // Start flattening

        // Return a new FeatureCollection with flattened features
        return {
            type: 'FeatureCollection',
            features: flattenedFeatures,
        };
    };

    const projectGeoJSONToMap = (geoJsonData) => {
        try {
            // Flatten the GeoJSON before processing
            const flattenedGeoJSON = flattenGeoJSON(geoJsonData);

            // Remove previous GeoJSON layer if it exists
            if (geoJsonLayerRef.current) {
                mapRef.current.removeLayer(geoJsonLayerRef.current);
            }

            // Store the GeoJSON data for download
            geoJsonDataRef.current = flattenedGeoJSON;

            // Use OpenLayers to read and project GeoJSON features
            const format = new GeoJSON();

            // Read the features from the flattened GeoJSON data
            const features = format.readFeatures(flattenedGeoJSON, {
                featureProjection: mapRef.current.getView().getProjection(),
            });

            if (features.length === 0) {
                throw new Error('No valid features to display');
            }

            // Define a style for the features
            const nonFuelStyle = new Style({
                stroke: new Stroke({
                    color: 'blue',
                    width: 2,
                }),
                fill: new Fill({
                    color: 'rgba(0, 0, 255, 0.5)',
                }),
            });

            // Create a vector source and layer to hold the features
            const nonFuelSource = new VectorSource({
                features: features,
            });

            const nonFuelLayer = new VectorLayer({
                source: nonFuelSource,
                style: nonFuelStyle,
            });

            // Add the new GeoJSON layer to the map
            mapRef.current.addLayer(nonFuelLayer);

            // Store the layer reference for future use
            geoJsonLayerRef.current = nonFuelLayer;
        } catch (error) {
            // Log detailed error message
            logger.error('Error projecting GeoJSON to map:', error);
        }
    };

    const downloadGeoJSON = () => {
        if (!geoJsonDataRef.current) {
            alert("No GeoJSON data available for download.");  // Show alert if no data is available
            return;
        }

        const geoJsonStr = JSON.stringify(geoJsonDataRef.current, null, 2);
        const blob = new Blob([geoJsonStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'non_fuel_areas.geojson';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleConfirm = async () => {

        // Create a new AbortController for this request
        const controller = new AbortController();
        const { signal } = controller;
        setAbortController(controller); // Save the controller to state

        // Reset previous errors
        setRadiusError('');
        setStartDateError('');
        setEndDateError('');
        setErrorMessage(''); // Reset error message

        // Set button loading state to true
        setButtonLoading(true);

        // Apply default radius if not provided
        const appliedRadius = radius ? parseFloat(radius) : 1;

        // Apply default dates if start date or end date is not fully provided
        const appliedStartDate = startDate || defaultStartDate;
        const appliedEndDate = endDate || getTodayDate();

        let isValid = true;

        // Validate radius
        if (isNaN(appliedRadius) || appliedRadius <= 0) {
            setRadiusError('Radius is invalid');
            isValid = false;
        }

        // Stop if validation fails
        if (!isValid) {
            return;
        }

        // Proceed with your existing handleConfirm logic
        const latNumber = parseFloat(manualLat);
        const lonNumber = parseFloat(manualLon);

        const jsonData = {
            lat: latNumber,
            lon: lonNumber,
            radius: appliedRadius,  // Send the radius in kilometers
            start_date: appliedStartDate,
            end_date: appliedEndDate
        };

        logger.log('Request JSON: ', jsonData);

        try {
            const response = await api.post('/map/non-fuel-areas/', jsonData, { signal });
            const geoJsonData = response.data;

            logger.log('GeoJsonData from API: ', geoJsonData);

            // Step 1: Create a circle using Turf.js (in EPSG:4326)
            const circleCenter = [lonNumber, latNumber];  // Center in EPSG:4326 (lon, lat)
            const circleRadiusInKm = appliedRadius; // Radius in kilometers

            // Create a turf circle in EPSG:4326
            const turfCircle = turf.circle(circleCenter, circleRadiusInKm, {
                units: 'kilometers',
            });

            // Step 2: Filter and clip features using Turf.js (still in EPSG:4326)
            const clippedFeatures = geoJsonData.features.reduce((acc, feature) => {
                const geometryType = feature.geometry.type;
                const turfFeature = turf.feature(feature.geometry);

                let clippedFeature = null;

                // Handle only Polygon and MultiPolygon geometries
                if (geometryType === 'Polygon' || geometryType === 'MultiPolygon') {
                    // Use turf.intersect for Polygons and MultiPolygons
                    clippedFeature = turf.intersect(turfCircle, turfFeature);
                }

                // If the feature is fully or partially inside the circle, the clipped feature will exist
                if (clippedFeature) {
                    // Maintain the properties of the original feature
                    const newFeature = {
                        ...feature, // Keep all original properties of the feature
                        geometry: clippedFeature.geometry, // Update the geometry with the clipped one
                    };

                    // Add the clipped feature to the result
                    acc.push(newFeature);
                }

                return acc;
            }, []);

            // Step 3: Create new GeoJSON data with filtered features
            const filteredGeoJsonData = {
                type: 'FeatureCollection',
                features: clippedFeatures,  // Add the clipped Polygon and MultiPolygon features
            };

            logger.log('Clipped GeoJsonData: ', filteredGeoJsonData);

            // Project the clipped GeoJSON to the map (in EPSG:4326)
            projectGeoJSONToMap(filteredGeoJsonData);  // Use the clipped GeoJSON data


            // Convert circle polygon to EPSG:3857 for visualization
            const format = new GeoJSON();
            const circleFeature = format.readFeature(turfCircle, {
                featureProjection: mapRef.current.getView().getProjection(), // This converts it to EPSG:3857 for display
            });

            // Define a style for the circle
            const circleStyle = new Style({
                stroke: new Stroke({
                    color: 'rgba(255, 0, 0, 0.2)',  // Red with transparency
                    width: 2,
                }),
                fill: new Fill({
                    color: 'rgba(128, 128, 128, 0.1)',  // Grey with transparency
                }),
            });

            // Apply the style to the circle
            circleFeature.setStyle(circleStyle);

            // Set `selectable: false` to make the circle unselectable
            circleFeature.setProperties({ selectable: false });

            // Add the circle to the map
            const vectorSource = mapRef.current.getLayers().item(1).getSource();

            // Remove the last drawn circle if it exists
            if (lastCircleFeatureRef.current) {
                vectorSource.removeFeature(lastCircleFeatureRef.current);
            };

            vectorSource.addFeature(circleFeature);

            lastCircleFeatureRef.current = circleFeature; // Store the last drawn circle

        } catch (error) {
            if (error.name === 'AbortError') {
                logger.log('Request was aborted');
            } else {
                logger.error('Error fetching GeoJSON data:', error);
                setErrorMessage('Failed to connect to the database. Please try again later.');
            }
        } finally {
            setButtonLoading(false); // Ensure button state is reset regardless of success or error
        }

        // Save the confirmed radius and dates
        setConfirmedRadius(appliedRadius);
        setConfirmedStartDate(appliedStartDate);
        setConfirmedEndDate(appliedEndDate);

        // Remove temporary feature and reset input fields
        if (tempFeatureRef.current) {
            mapRef.current.getLayers().item(1).getSource().removeFeature(tempFeatureRef.current);
            tempFeatureRef.current = null;
        };

        setShowDialog(false);
        setRadius('');
        setManualLon(''); // Reset the manual longitude input after confirmation
        setManualLat(''); // Reset the manual latitude input after confirmation
        setStartDate(''); // Reset the start date after confirmation
        setEndDate('');   // Reset the end date after confirmation
        setHoverLock(false);  // Unlock hover information
        setHoveredButtonInfo('<strong>Hover over a button to see its details</strong>'); // Reset hover info

        // Deactivate the draw point toggle and remove interaction from the map
        if (drawPointToggleRef.current && drawPointInteractionRef.current) {
            drawPointToggleRef.current.setActive(false);  // Deactivate the toggle
            mapRef.current.removeInteraction(drawPointInteractionRef.current);
        };
    };

    const handleCancel = () => {
        // Abort any ongoing request if it's still loading
        if (isButtonLoading && abortController) {
            abortController.abort(); // Cancel the ongoing request
            setButtonLoading(false); // Stop the loading state
        }

        // Remove the pin (last drawn point) if it exists, but keep all other features like the circle and previous results
        const pointSource = mapRef.current.getLayers().item(1).getSource(); // Assuming pointSource is the second layer
        if (drawnLon && drawnLat) {
            const features = pointSource.getFeatures();
            features.forEach((feature) => {
                const coords = toLonLat(feature.getGeometry().getCoordinates());
                const [lon, lat] = coords;

                // Remove the pin that was drawn (matches drawnLon and drawnLat)
                if (lon === drawnLon && lat === drawnLat) {
                    pointSource.removeFeature(feature); // Remove this feature (the drawn pin)
                }
            });
        }

        // Ensure the last circle remains on the map
        if (lastCircleFeatureRef.current) {
            const source = mapRef.current.getLayers().item(1).getSource();
            const features = source.getFeatures();
            let lastCircleExists = false;

            // Check if the last circle feature is still on the map
            features.forEach((feature) => {
                if (feature === lastCircleFeatureRef.current) {
                    lastCircleExists = true;
                }
            });

            // If the last circle is not on the map, add it back
            if (!lastCircleExists) {
                source.addFeature(lastCircleFeatureRef.current); // Add the last circle back if it was removed
            }
        }

        // Reset the dialog and form states
        setShowDialog(false);
        setRadius('');
        setManualLon('');
        setManualLat('');
        setStartDate('');
        setEndDate('');

        // Remove the draw point interaction from the map (stopping further drawing)
        if (drawPointInteractionRef.current) {
            mapRef.current.removeInteraction(drawPointInteractionRef.current);  // Remove the draw interaction
        }

        // Ensure the draw point toggle is fully closed (inactive)
        if (drawPointToggleRef.current) {
            drawPointToggleRef.current.setActive(false);  // Deactivate the toggle
        }

        // Stop the loading state (after removing interaction and toggles)
        setButtonLoading(false);
        setHoverLock(false);  // Unlock hover information
        setHoveredButtonInfo('<strong>Hover over a button to see its details</strong>'); // Reset hover info
    };    

    // Handle dialog draggability
    useEffect(() => {
        const dialog = dialogRef.current;
        let isDragging = false;
        let offsetX, offsetY;

        const handleMouseDown = (e) => {
            isDragging = true;
            offsetX = e.clientX - dialog.getBoundingClientRect().left;
            offsetY = e.clientY - dialog.getBoundingClientRect().top;
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
        };

        const handleMouseMove = (e) => {
            if (isDragging) {
                const left = e.clientX - offsetX;
                const top = e.clientY - offsetY;
                dialog.style.left = `${left}px`;
                dialog.style.top = `${top}px`;
                dialog.style.transform = `translate(0, 0)`; // Remove center alignment when moving
            }
        };

        const handleMouseUp = () => {
            isDragging = false;
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };

        if (dialog) {
            dialog.addEventListener("mousedown", handleMouseDown);
        }

        return () => {
            if (dialog) {
                dialog.removeEventListener("mousedown", handleMouseDown);
            }
        };
    }, [showDialog]);

    const handleLatChange = (e) => {
        const value = parseFloat(e.target.value);
        setManualLat(e.target.value);

        if (isNaN(value) || value < -90 || value > 90) {
            setLatError('Please enter a valid latitude (-90 to 90).');
        } else {
            setLatError(''); // Clear the error if valid
        }
    };

    const handleLonChange = (e) => {
        const value = parseFloat(e.target.value);
        setManualLon(e.target.value);

        if (isNaN(value) || value < -180 || value > 180) {
            setLonError('Please enter a valid longitude (-180 to 180).');
        } else {
            setLonError(''); // Clear the error if valid
        }
    };

    logger.log("MapRef current (before rendering SideBar):", mapRef.current);

    return (
        <>            
            <div id="map"></div>           

            {isMapInitialized && (
                <SideBar
                    mapRef={mapRef}
                    isLoggedIn={isLoggedIn}
                    setIsLoggedIn={setIsLoggedIn}
                    setHoveredButtonInfo={setHoveredButtonInfo}
                    toggleDrawer={toggleDrawer}
                />
            )}

            {isMapInitialized && mapRef.current && selectToViewRef.current && (
                <FeaturePopup 
                    map={mapRef.current} 
                    selectInteraction={selectToViewRef.current} 
                    popupOverlayRef={popupOverlayRef} 
                />
            )}

            {drawnLon && drawnLat && (
                <div className="coordinates-container">
                    <div className="title">
                        <h2>Non Fuel Areas</h2>
                    </div>
                    <div className="coordinates">
                        <h3>Drawn Point Coordinates:</h3>
                        <p>Latitude: {drawnLat}</p>
                        <p>Longitude: {drawnLon}</p>
                        <p>Radius: {confirmedRadius} km</p>
                        <p>Start Date: {confirmedStartDate}</p>
                        <p>End Date: {confirmedEndDate}</p>
                    </div>
                </div>
            )}

            {showDialog && (
                <div className="non-fuel-dialog" ref={dialogRef}>
                    <h2>Enter Details</h2>
                    <p>Please enter the latitude and longitude:</p>

                    <input
                        type="number"
                        id="lat-input"
                        value={manualLat}
                        onChange={handleLatChange}
                        placeholder="Enter latitude"
                        disabled={isButtonLoading}
                    />
                    {latError && <p className="error-message">{latError}</p>}

                    <input
                        type="number"
                        id="lon-input"
                        value={manualLon}
                        onChange={handleLonChange}
                        placeholder="Enter longitude"
                        disabled={isButtonLoading}
                    />
                    {lonError && <p className="error-message">{lonError}</p>}

                    <p>Please enter the radius (in kilometers):</p>
                    <input
                        type="number"
                        id="radius-input"
                        value={radius}
                        onChange={(e) => setRadius(e.target.value)}
                        placeholder="Radius in kilometers"
                        disabled={isButtonLoading}
                    />
                    <p>Start Date:</p>
                    <input
                        type="date"
                        id="start-date-input"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        disabled={isButtonLoading}
                    />
                    <p>End Date:</p>
                    <input
                        type="date"
                        id="end-date-input"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        disabled={isButtonLoading}
                    />
                    <div className="dialog-buttons">
                        <button id="cancel-btn" onClick={handleCancel}>
                            Cancel
                        </button>
                        <button
                            id="confirm-btn"
                            onClick={handleConfirm}
                            disabled={isButtonLoading || latError || lonError || !manualLat || !manualLon}
                        >
                            {isButtonLoading ? (
                                <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                                'Confirm'
                            )}
                        </button>
                    </div>
                </div>
            )}

            {errorMessage && (
                <div className="error-alert">
                    <p>{errorMessage}</p>
                </div>
            )}                     
            
            {/* Information Drawer */}
            <div className={`information-container ${isDrawerOpen ? 'open' : ''}`}>
                <div className="title">
                    <h2>Instructions</h2>
                </div>
                <div className="information-content">
                    <p dangerouslySetInnerHTML={{ __html: hoveredButtonInfo }} />
                </div>
            </div>           
        </>
    );
};

export default NonFuelAreas;